import { Text } from '@fullstory/components';
import React, { PropsWithChildren, useCallback } from 'react';

export interface MiddleEllipsisProps
  extends Pick<React.ComponentProps<typeof Text>, 'appearance' | 'color'> {
  // TODO(johnverrone): add support for dynamic maxLines
  maxLines: 2;
  maxWidth?: number;
}

export const MiddleEllipsis: React.FC<PropsWithChildren<MiddleEllipsisProps>> = props => {
  const measuredParent = useCallback(
    (node: HTMLDivElement) => {
      const prepEllipse = (node: HTMLElement) => {
        const parent = node.parentNode as HTMLElement;
        const child = node.children[0] as HTMLElement;

        if (parent !== null && child !== null) {
          ellipse(
            // Use the smaller width.
            node.offsetWidth > parent.offsetWidth ? parent : node,
            child,
            props.maxLines,
            props.maxWidth,
          );
        }
      };

      if (node !== null) {
        prepEllipse(node);
      }
    },
    [props.maxLines, props.maxWidth],
  );

  return (
    <div
      data-component="MiddleEllipsis"
      ref={measuredParent}
      style={{
        wordBreak: 'break-all',
        overflowWrap: 'normal',
      }}
    >
      <Text tag="span" appearance={props.appearance} color={props.color}>
        {props.children}
      </Text>
    </div>
  );
};

function ellipse(
  parentNode: HTMLElement,
  childNode: HTMLElement,
  maxLines: number = 2,
  maxWidth?: number,
) {
  const childWidth = childNode.offsetWidth;
  const containerWidth = maxWidth ? maxWidth : parentNode.offsetWidth;
  if (childWidth > containerWidth) {
    const str = childNode.textContent ?? '';
    const txtChars = str.length;
    const avgLetterSize = childWidth / txtChars;
    const fitPerLine = containerWidth / avgLetterSize;
    const canFit = fitPerLine * maxLines;
    const delEachSide = (txtChars - canFit + 15) / 2;
    const endLeft = Math.floor(txtChars / 2 - delEachSide);
    const startRight = Math.max(Math.ceil(txtChars / 2 + delEachSide), endLeft);

    childNode.setAttribute('data-original', childNode.textContent ?? '');
    childNode.innerHTML = `${str.substr(0, endLeft)}...<br />${str.substr(startRight)}`;
  }
}
